import fs from 'node:fs'
import path from 'node:path'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import type { GeneratedFile } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { toCamelCase } from '@core-engine/utils/string.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace.js'
import { resolveProjectPaths } from '@next-adapter/config/paths.js'
import { namespaceGeneratedFiles } from '@next-adapter/generators/namespace-output.js'

interface NamespacedExtract {
  namespace: string
  name: string
  path: string
}

function listExtractNames(cwd: string, dirPath: string, ignored = new Set<string>()): string[] {
  const fullDir = path.join(cwd, ...dirPath.split('/'))
  if (!fs.existsSync(fullDir)) {
    return []
  }

  return fs
    .readdirSync(fullDir)
    .filter((fileName) => fileName.endsWith('.ts'))
    .map((fileName) => fileName.replace(/\.ts$/, ''))
    .filter((name) => !ignored.has(name))
    .sort()
}

function listNamespacedExtracts(
  cwd: string,
  dirPath: string,
  ignoredNamespaces = new Set<string>()
): NamespacedExtract[] {
  const fullDir = path.join(cwd, ...dirPath.split('/'))
  if (!fs.existsSync(fullDir)) {
    return []
  }

  const extracts: NamespacedExtract[] = []
  for (const namespace of fs.readdirSync(fullDir).sort()) {
    if (ignoredNamespaces.has(namespace)) {
      continue
    }

    const namespaceDir = path.join(fullDir, namespace)
    if (!fs.existsSync(namespaceDir) || !fs.statSync(namespaceDir).isDirectory()) {
      continue
    }

    for (const fileName of fs.readdirSync(namespaceDir).sort()) {
      if (!fileName.endsWith('.ts')) {
        continue
      }

      const name = fileName.replace(/\.ts$/, '')
      extracts.push({
        namespace,
        name,
        path: `${dirPath}/${namespace}/${fileName}`
      })
    }
  }

  return extracts.sort((a, b) =>
    `${a.namespace}/${a.name}`.localeCompare(`${b.namespace}/${b.name}`)
  )
}

function renderDbBarrel(dbExtracts: NamespacedExtract[]): string {
  const exportLines = dbExtracts.map(
    (extract) => `export * from './${extract.namespace}/${extract.name}'`
  )
  return `export * from './core/schema'
${exportLines.length > 0 ? `\n${exportLines.join('\n')}\n` : '\n'}`
}

function renderNavigationBarrel(navExtractNames: string[], namespaceValue: string): string {
  const namespace = resolveAdminNamespace(namespaceValue)
  const importLines = navExtractNames.map(
    (name) => `import { ${toCamelCase(name)}Nav } from './navigation/${name}'`
  )
  const spreadLines = navExtractNames.map((name) => `  ...${toCamelCase(name)}Nav`)
  const extractImports = importLines.length > 0 ? `\n${importLines.join('\n')}` : ''

  return `import { House, ImagePlay } from 'lucide-react'
import type { ${namespace.pascal}NavigationItem } from '${namespace.alias}/types/navigation'${extractImports}

export type { ${namespace.pascal}NavigationItem } from '${namespace.alias}/types/navigation'

const coreNav: ${namespace.pascal}NavigationItem[] = [
  {
    label: 'Overview',
    href: '${namespace.routePath}',
    icon: House,
    group: 'General',
    position: 0,
  },
  {
    label: 'Media',
    href: '${namespace.routePath}/media',
    icon: ImagePlay,
    group: 'General',
    position: 1,
  },
]

function comparePositions(a?: number, b?: number): number {
  if (a != null && b != null) return a - b
  if (a != null) return -1
  if (b != null) return 1
  return 0
}

const combinedNavigation = [
  ...coreNav${spreadLines.length > 0 ? `,\n${spreadLines.join(',\n')}` : ''}
]

const dashboard = combinedNavigation.find((item) => item.href === '${namespace.routePath}')
const others = combinedNavigation.filter((item) => item.href !== '${namespace.routePath}')

others.sort((a, b) => {
  if (!a.group && b.group) return -1
  if (a.group && !b.group) return 1
  if (a.group && b.group && a.group !== b.group) {
    return comparePositions(a.groupPosition, b.groupPosition) || a.group.localeCompare(b.group)
  }
  return comparePositions(a.position, b.position) || a.label.localeCompare(b.label)
})

export const ${namespace.camel}Navigation = [...(dashboard ? [dashboard] : []), ...others]
`
}

function renderWebhookEventsBarrel(extractNames: string[], namespaceValue: string): string {
  const namespace = resolveAdminNamespace(namespaceValue)
  const importLines = extractNames.map(
    (name) => `import { ${toCamelCase(name)}WebhookEvents } from './webhook-events/${name}'`
  )
  const entryLines = extractNames.map((name) => `  ${toCamelCase(name)}WebhookEvents`)
  const extractImports = importLines.length > 0 ? `\n${importLines.join('\n')}` : ''

  const entries = entryLines.length > 0 ? `[\n${entryLines.join(',\n')}\n]` : '[]'

  return `import type { WebhookEventSource } from '${namespace.alias}/types/webhooks'${extractImports}

export type { WebhookEventSource } from '${namespace.alias}/types/webhooks'

export const webhookEventSources: WebhookEventSource[] = ${entries}
`
}

export function renderBarrelFiles(cwd: string, config: ResolvedBetterstartConfig): GeneratedFile[] {
  const paths = resolveProjectPaths(config)
  const dbExtracts = listNamespacedExtracts(cwd, paths.adminDbDir).filter(
    (extract) => !(extract.namespace === 'core' && extract.name === 'schema')
  )
  const navExtractNames = listExtractNames(cwd, paths.adminNavigationDir, new Set(['core']))
  const webhookEventExtractNames = listExtractNames(cwd, paths.adminWebhookEventsDir)

  return namespaceGeneratedFiles(
    [
      createGeneratedFile(paths.adminDbSchemaFile, renderDbBarrel(dbExtracts)),
      createGeneratedFile(
        paths.adminNavigationFile,
        renderNavigationBarrel(navExtractNames, config.frameworkConfig.next.namespace)
      ),
      createGeneratedFile(
        paths.adminWebhookEventsFile,
        renderWebhookEventsBarrel(webhookEventExtractNames, config.frameworkConfig.next.namespace)
      )
    ],
    config.frameworkConfig.next.namespace
  )
}
